/**
 * zzai-cart.js
 * Cart drawer logic for ZZAI Horizon theme.
 * Depends on zzai-core.js (loaded in layout/theme.liquid before this section script).
 * Vanilla JS only — no frameworks, no jQuery.
 */

/** Horizon listens for this to refresh the cart bubble and cart sections. */
const THEME_CART_UPDATE = 'cart:update';

/**
 * POST JSON to cart endpoints and require a JSON body + 2xx response.
 * @param {string} url
 * @param {object} body
 * @returns {Promise<object>}
 */
function parseCartJsonBody(text, res) {
  const t = (text || '').trim();
  if (!t) {
    if (!res.ok) throw new Error(`${res.status} ${res.statusText || ''}`.trim());
    return { items: [], item_count: 0, total_price: 0 };
  }
  try {
    return JSON.parse(t);
  } catch {
    throw new Error(
      (t.startsWith('<') ? 'Cart returned HTML (password page, proxy error, or login).' : 'Invalid JSON: ') +
        t.slice(0, 160),
    );
  }
}

function cartApiError(message, res, text) {
  const err = new Error(message);
  if (res && res.status) err.status = res.status;
  const t = (text || '').trim();
  err.isCartHtmlResponse = t.startsWith('<');
  err.isBotChallenge =
    err.isCartHtmlResponse && /verifying your connection|cf-browser-verification|challenge-platform/i.test(t);
  return err;
}

async function cartJsonPost(url, body) {
  const res = await ZZAI.jsonPost(url, body);
  const text = await res.text();
  if (!res.ok) {
    const t = (text || '').trim();
    if (t.startsWith('<') || /verifying your connection/i.test(t)) {
      throw cartApiError(
        "We couldn't update that item. Please refresh or contact us.",
        res,
        text,
      );
    }
    throw cartApiError(t.slice(0, 240) || `${res.status} ${res.statusText || ''}`.trim(), res, text);
  }
  return parseCartJsonBody(text, res);
}

function dispatchThemeCartUpdate(itemCount) {
  document.dispatchEvent(
    new CustomEvent(THEME_CART_UPDATE, {
      bubbles: true,
      detail: {
        resource: null,
        sourceId: 'zzai-cart',
        data: { itemCount: Number(itemCount) || 0 },
      },
    }),
  );
}

/** `zzai.brand_color` hex per handle — from Liquid `#zzai-brand-by-handle` (see zzai-brand-palette-json). */
let zzaiBrandByHandleCache;

function readBrandByHandle() {
  if (zzaiBrandByHandleCache) return zzaiBrandByHandleCache;
  const el = document.getElementById('zzai-brand-by-handle');
  try {
    zzaiBrandByHandleCache = JSON.parse((el?.textContent || '{}').trim() || '{}');
  } catch {
    zzaiBrandByHandleCache = {};
  }
  return zzaiBrandByHandleCache;
}

/** Canonical short names — from Liquid `#zzai-product-names-by-handle`. */
let zzaiNameByHandleCache;

function readNameByHandle() {
  if (zzaiNameByHandleCache) return zzaiNameByHandleCache;
  const el = document.getElementById('zzai-product-names-by-handle');
  try {
    zzaiNameByHandleCache = JSON.parse((el?.textContent || '{}').trim() || '{}');
  } catch {
    zzaiNameByHandleCache = {};
  }
  return zzaiNameByHandleCache;
}

function displayNameForHandle(handle, product) {
  const h = String(handle || product?.handle || '').trim();
  const map = readNameByHandle();
  if (h && map[h]) return map[h];
  const raw = product?.title || '';
  const short = raw.split(/\s[—–-]\s/)[0].replace(/\s+Drink$/, '').trim();
  return short || raw || h;
}

function lineItemDisplayName(item) {
  const h = String(item?.handle || item?.product_handle || productHandleFromLineItem(item) || '').trim();
  return displayNameForHandle(h, { title: item?.product_title || item?.title, handle: h });
}

function productHandleFromLineItem(item) {
  const u = item.url || '';
  const m = String(u).match(/\/products\/([^/?#]+)/);
  if (m) return decodeURIComponent(m[1]);
  if (item.product_handle) return String(item.product_handle);
  return '';
}

/** Relative product URL for a cart line (`item.url` from `/cart.js`). */
function lineItemProductHref(item) {
  let u = String(item?.url || '').trim();
  if (u) {
    if (/^javascript:/i.test(u)) return '';
    if (/^https?:\/\//i.test(u)) {
      try {
        const parsed = new URL(u);
        return parsed.pathname + (parsed.search || '');
      } catch {
        return '';
      }
    }
    return u.startsWith('/') ? u : `/${u}`;
  }
  const h = String(item?.handle || item?.product_handle || '').trim();
  if (h) return `/products/${encodeURIComponent(h)}`;
  return '';
}

/**
 * One delegated listener on `.zzai-cart-section` so qty / remove / upgrade / cadence / close
 * keep working after `innerHTML` swaps (and so a failed refresh cannot strand the UI).
 */
function zzaiCartRoot() {
  return (
    document.getElementById('zzai-cart-overlay')?.parentElement ||
    document.getElementById('zzai-cart-drawer')?.parentElement ||
    document.querySelector('.zzai-cart-section')
  );
}

function zzaiCartCustomerUpdateError() {
  return "We couldn't update that item. Please refresh or contact us.";
}

function setupZzaiCartDelegationOnce() {
  const root = zzaiCartRoot();
  if (!root || root.dataset.zzaiDelegation === '1') return;
  root.dataset.zzaiDelegation = '1';

  root.addEventListener('click', e => {
    const drawer = document.getElementById('zzai-cart-drawer');
    if (!drawer?.classList.contains('is-open')) return;

    if (e.target.closest('#zzai-cart-overlay')) {
      closeCartDrawer();
      return;
    }

    if (e.target.closest('.zzai-cart-drawer__close')) {
      closeCartDrawer();
      return;
    }

    const addBtn = e.target.closest('#cart-cross-sells .add-btn');
    if (addBtn) {
      addCrossSellFromButton(addBtn);
      return;
    }

    const nudgeAll = e.target.closest('#zzai-sub-nudge');
    if (nudgeAll) {
      const label = nudgeAll.textContent;
      nudgeAll.disabled = true;
      upgradeAllOneTimeLines()
        .finally(() => {
          nudgeAll.disabled = false;
          nudgeAll.textContent = label;
        });
      return;
    }

    const lines = document.getElementById('zzai-cart-lines');
    if (!lines?.contains(e.target)) return;

    const minus = e.target.closest('.qty-minus');
    if (minus) {
      const qty  = parseInt(minus.dataset.qty,  10);
      const step = Math.max(1, parseInt(minus.dataset.step || '1', 10) || 1);
      debouncedChangeQty(minus.dataset.key, Math.max(0, qty - step));
      return;
    }

    const plus = e.target.closest('.qty-plus');
    if (plus) {
      const qty  = parseInt(plus.dataset.qty,  10);
      const step = Math.max(1, parseInt(plus.dataset.step || '1', 10) || 1);
      debouncedChangeQty(plus.dataset.key, qty + step);
      return;
    }

    const rem = e.target.closest('.cart-line__remove');
    if (rem) {
      removeItem(rem.dataset.key);
      return;
    }

    const up = e.target.closest('.upgrade-btn');
    if (up) {
      const label = up.textContent;
      up.disabled = true;
      up.textContent = '…';
      upgradeToSubscription(up.dataset.key, up.dataset.qty)
        .finally(() => {
          up.disabled = false;
          up.textContent = label;
        });
    }
  });

  root.addEventListener('change', e => {
    const drawer = document.getElementById('zzai-cart-drawer');
    if (!drawer?.classList.contains('is-open')) return;
    const sel = e.target.closest('.cadence-select');
    if (!sel || !document.getElementById('zzai-cart-lines')?.contains(sel)) return;

    const key = sel.dataset.key;
    const qty = parseInt(sel.dataset.qty, 10);
    (async () => {
      try {
        const payload = { id: key, quantity: qty };
        if (sel.value === 'onetime') payload.selling_plan = null;
        else {
          const sp = sellingPlanForApi(sel.value);
          if (sp == null) throw new Error('Invalid selling plan selection');
          payload.selling_plan = sp;
        }
        await cartJsonPost(ZZAI.cartChangeJsUrl(), payload);
        await refreshCart();
      } catch (err) {
        console.error('[zzai-cart] cadence change failed:', err);
        alert(zzaiCartCustomerUpdateError());
      }
    })();
  });
}

async function addCrossSellFromButton(btn) {
  btn.disabled = true;
  const prev = btn.textContent;
  btn.textContent = '…';
  try {
    await cartJsonPost(ZZAI.cartAddJsUrl(), {
      items: [{ id: parseInt(btn.dataset.variant, 10), quantity: 1 }],
    });
    await refreshCart();
  } catch (err) {
    console.error('[zzai-cart] cross-sell add failed:', err);
    btn.disabled = false;
    btn.textContent = prev;
  }
}

/* ─── Drawer open / close ─────────────────────────────────────────────────── */

let _cartDrawerOpener = null;

function openCartDrawer() {
  if (document.documentElement.dataset.zzaiPledgeCheckout === '1') return;
  const drawer  = document.getElementById('zzai-cart-drawer');
  const overlay = document.getElementById('zzai-cart-overlay');
  if (!drawer) return;
  _cartDrawerOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  drawer.classList.add('is-open', 'open');
  drawer.setAttribute('aria-hidden', 'false');
  drawer.removeAttribute('inert');
  if (overlay) {
    overlay.classList.add('is-open', 'open');
    overlay.setAttribute('aria-hidden', 'false');
  }
  document.body.style.overflow = 'hidden';
  drawer.querySelector('.cart-drawer-scroll, .cart-items')?.scrollTo?.(0, 0);
  drawer.querySelector('.cart-close, .zzai-cart-drawer__close')?.focus();
  refreshCart();
}

function closeCartDrawer() {
  const drawer  = document.getElementById('zzai-cart-drawer');
  const overlay = document.getElementById('zzai-cart-overlay');
  if (!drawer) return;
  if (_cartDrawerOpener && typeof _cartDrawerOpener.focus === 'function') {
    _cartDrawerOpener.focus({ preventScroll: true });
  } else {
    document.body.focus();
  }
  _cartDrawerOpener = null;
  drawer.classList.remove('is-open', 'open');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.setAttribute('inert', '');
  if (overlay) {
    overlay.classList.remove('is-open', 'open');
    overlay.setAttribute('aria-hidden', 'true');
  }
  document.body.style.overflow = '';
}

/* ─── Cart data ───────────────────────────────────────────────────────────── */

async function refreshCart() {
  let cart;
  try {
    const res = await fetch(ZZAI.cartJsUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(text.slice(0, 240) || `${res.status} ${res.statusText}`);
    }
    cart = parseCartJsonBody(text, res);
    if (cartHasCartLevelDiscount(cart) && !cartHasPledgeMarker(cart)) {
      cart = await cartJsonPost(ZZAI.cartUpdateJsUrl(), {
        attributes: { pledge_ks_id: '', pledge_entitlement: '', pledge_total_packs: '' },
        discount: '',
      });
    }
  } catch (err) {
    console.error('[zzai-cart] refreshCart failed:', err);
    return;
  }

  await renderLineItems(cart);
  updateCartTotals(cart);
  updateShippingBar(cart);
  updateCartHeader(cart);
  updateSubscribeNudge(cart);
  loadCrossSells(cart);
  toggleCrossSellVisibility(cart);
  dispatchThemeCartUpdate(cart.item_count);
}

/* ─── Product JSON cache (selling plans per variant) ─────────────────────── */

const productJsonCache = new Map();
let lastCrossSellHandlesKey = '';

async function getProductJson(handle) {
  if (!handle || productJsonCache.has(handle)) return productJsonCache.get(handle);
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers:     { Accept: 'application/json' },
    });
    const data = res.ok ? await res.json() : null;
    productJsonCache.set(handle, data);
    return data;
  } catch {
    productJsonCache.set(handle, null);
    return null;
  }
}

function variantSellingPlans(variant) {
  const raw = variant?.selling_plan_allocations;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const seen = new Set();
  const out = [];
  const labelFn =
    window.ZZAI && typeof window.ZZAI.sellingPlanDisplayLabel === 'function'
      ? window.ZZAI.sellingPlanDisplayLabel.bind(window.ZZAI)
      : a => String(a?.selling_plan?.name || a?.name || '').trim();

  for (const a of raw) {
    const id = a.selling_plan_id ?? a.selling_plan?.id;
    if (id == null || seen.has(String(id))) continue;
    seen.add(String(id));
    const sp = a.selling_plan || {};
    const options =
      window.ZZAI && typeof window.ZZAI.normalizePlanOptions === 'function'
        ? window.ZZAI.normalizePlanOptions(sp.options)
        : Array.isArray(sp.options)
          ? sp.options
          : [];
    const name = labelFn({ selling_plan: sp, options }) || String(sp.name || '').trim();
    out.push({ id, name, options, selling_plan: sp });
  }
  return out;
}

let zzaiSellingPlansConfigCache;

function readSellingPlansConfig() {
  if (zzaiSellingPlansConfigCache) return zzaiSellingPlansConfigCache;
  const el = document.getElementById('zzai-selling-plans-config');
  try {
    const raw = JSON.parse((el?.textContent || '{}').trim() || '{}');
    zzaiSellingPlansConfigCache =
      window.ZZAI && typeof window.ZZAI.normalizeSellingPlans === 'function'
        ? window.ZZAI.normalizeSellingPlans(raw)
        : raw;
  } catch {
    zzaiSellingPlansConfigCache = { plans: [], default: '' };
  }
  return zzaiSellingPlansConfigCache;
}

function fallbackPlansFromStore() {
  const cfg = readSellingPlansConfig();
  return (cfg.plans || []).map(p => ({
    id: p.id,
    name: p.displayName || p.name || '',
    options: p.options || [],
  }));
}

/** Shopify product.js often omits selling_plan.name; merchants also use the word "Subscription" for every plan. */
function isGenericPlanLabel(name) {
  const s = String(name || '').trim();
  if (!s) return true;
  if (/^subscription\.?$/i.test(s)) return true;
  if (/^subscribe\s*&\s*save/i.test(s) && !/\d/.test(s) && !/\b(week|month|day)s?\b/i.test(s)) return true;
  return false;
}

/** Prefer "Delivered every N weeks" when the string clearly states an interval. */
function humanizeCadenceLabel(name) {
  const s = String(name || '').trim();
  if (!s) return s;
  if (/^delivered\s+every/i.test(s)) return s;
  let m = s.match(/^delivery\s+every\s+(\d+)\s*(week|month|day)s?\b/i);
  if (m) return `Delivered every ${m[1]} ${m[2].toLowerCase()}${Number(m[1]) === 1 ? '' : 's'}`;
  m = s.match(/\bevery\s+(\d+)\s*(week|month|day)s?\b/i);
  if (m) return `Delivered every ${m[1]} ${m[2].toLowerCase()}${Number(m[1]) === 1 ? '' : 's'}`;
  return s;
}

function cadenceSortKey(plan) {
  const m = String(plan.name || '').match(/(\d+)\s*(week|month|day)/i);
  if (!m) return 999;
  const n = parseInt(m[1], 10);
  const u = m[2].toLowerCase();
  const unit = u.startsWith('day') ? 1 : u.startsWith('week') ? 7 : 30;
  return n * unit;
}

/**
 * Map plan IDs to theme dataset labels when the API gives no useful name.
 */
function enrichSellingPlanLabels(plans) {
  const storeById = new Map(fallbackPlansFromStore().map(p => [String(p.id), p]));
  const seen = new Set();
  const out = [];
  const labelFn =
    window.ZZAI && typeof window.ZZAI.sellingPlanDisplayLabel === 'function'
      ? window.ZZAI.sellingPlanDisplayLabel.bind(window.ZZAI)
      : null;

  for (const p of plans) {
    const idStr = String(p.id);
    if (seen.has(idStr)) continue;
    seen.add(idStr);
    let name = (p.name || '').trim();
    if (labelFn) {
      name =
        labelFn({ selling_plan: p.selling_plan || p, name: p.name, options: p.options }) ||
        name;
    }
    const fb = storeById.get(idStr);
    if (fb && (isGenericPlanLabel(name) || !name)) name = fb.name || name;
    else if (!name && fb) name = fb.name;
    else if (!name) name = fb?.name || 'Subscription';
    else if (!isGenericPlanLabel(name)) name = humanizeCadenceLabel(name);
    else if (fb) name = fb.name;
    out.push({ id: p.id, name });
  }
  out.sort((a, b) => cadenceSortKey(a) - cadenceSortKey(b));
  return out;
}

function lineItemProductHandle(item) {
  return String(item?.handle || item?.product_handle || productHandleFromLineItem(item) || '').trim();
}

async function plansForLineItem(item, container) {
  const handle = lineItemProductHandle(item);
  const product = handle ? await getProductJson(handle) : null;
  const variantId = Number(item.variant_id);
  const variant = product?.variants?.find(
    v => Number(v.id) === variantId || String(v.id) === String(item.variant_id),
  );
  let plans = variantSellingPlans(variant);

  if (product && window.ZZAI && typeof window.ZZAI.collectSellingPlansFromProduct === 'function') {
    const collected = window.ZZAI.collectSellingPlansFromProduct(product);
    if (collected.length) {
      const byVariant = new Map(variantSellingPlans(variant).map(p => [String(p.id), p]));
      plans = collected.map(p => {
        const idStr = String(p.id);
        const fromVar = byVariant.get(idStr);
        const labelFn =
          typeof window.ZZAI.sellingPlanDisplayLabel === 'function'
            ? window.ZZAI.sellingPlanDisplayLabel.bind(window.ZZAI)
            : null;
        const name =
          (labelFn &&
            labelFn({ selling_plan: p.selling_plan, options: p.options, name: p.name })) ||
          fromVar?.name ||
          p.name ||
          '';
        return {
          id: p.id,
          name,
          options: p.options,
          selling_plan: p.selling_plan,
        };
      });
    }
  }

  if (plans.length === 0) plans = fallbackPlansFromStore();
  plans = enrichSellingPlanLabels(plans);
  return { plans, variant };
}

/** Shopify selling plan IDs can exceed Number.MAX_SAFE_INTEGER — keep as string when needed. */
function sellingPlanForApi(idStr) {
  const s = String(idStr ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  if (Number.isFinite(n) && Number.isSafeInteger(n) && n > 0) return n;
  return s;
}

/**
 * Choose a selling_plan id that is valid for this cart line’s variant.
 * Prefers theme “default” plan only if it appears in the line’s available plans.
 */
async function pickSellingPlanForLine(item, container) {
  const { plans } = await plansForLineItem(item, container);
  if (!plans.length) return null;
  const pref = String(container?.dataset?.planId || readSellingPlansConfig().default || '').trim();
  if (pref && plans.some(p => String(p.id) === pref)) return String(pref);
  return String(plans[0].id);
}

/* ─── Line items ──────────────────────────────────────────────────────────── */

async function renderLineItems(cart) {
  const container = document.getElementById('zzai-cart-lines');
  if (!container) return;

  if (!cart.items || cart.items.length === 0) {
    const shopUrl =
      document.querySelector('.zzai-cart-section')?.dataset?.shopUrl || '/collections/all';
    container.innerHTML = `<div class="cart-empty">
      <span class="big">Tray's empty.</span>
      <p>Pick a flavor — your future self will thank you.</p>
      <a class="btn primary" href="${escHtml(shopUrl)}">Shop the cans</a>
    </div>`;
    return;
  }

  const subPct = readSubDiscountLabel();
  const rows = await Promise.all(
    cart.items.map(async (item, idx) => {
      const cadenceId = `cart-cadence-${idx}`;
      const ph = productHandleFromLineItem(item);
      const lineSku = ZZAI.flavorSkuFromHandle(ph);
      const skuAttr = lineSku ? ` data-sku="${escHtml(lineSku)}"` : '';
      const hasPlan = !!item.selling_plan_allocation;
      const currentPlanId = hasPlan
        ? String(item.selling_plan_allocation.selling_plan.id)
        : '';

      const orig = item.original_line_price ?? item.line_price ?? item.final_line_price;
      const fin  = item.final_line_price;
      const showDiscount = orig > fin && fin >= 0;
      const offPct = showDiscount && orig > 0
        ? Math.round((1 - fin / orig) * 100)
        : 0;

      const imgSrc = item.image || '';
      const lineHref = lineItemProductHref(item);
      const lineName = lineItemDisplayName(item);
      const imgBlock = imgSrc
        ? `<img src="${escHtml(imgSrc)}" alt="${escHtml(lineName)}" width="84" height="100" loading="lazy">`
        : '';
      const titleHtml = lineHref
        ? `<a href="${escHtml(lineHref)}">${escHtml(lineName)}</a>`
        : escHtml(lineName);
      const subLine = String(item.properties?._pack_display || item.variant_title || '').trim();
      const subHtml =
        subLine && subLine !== 'Default Title'
          ? `<p class="sub">${escHtml(subLine)}</p>`
          : '';
      const { plans } = await plansForLineItem(item, container);

      let subscribeHtml = '';
      if (plans.length > 0) {
        if (hasPlan) {
          const planOpts = plans
            .map(
              p =>
                `<option value="${String(p.id)}"${String(p.id) === currentPlanId ? ' selected' : ''}>${escHtml(p.name)}</option>`,
            )
            .join('');
          subscribeHtml = `
          <div class="cart-item-subscribe">
            <label class="cadence-label" for="${cadenceId}">Purchase option</label>
            <select id="${cadenceId}" class="cadence-select" data-key="${escHtml(item.key)}" data-qty="${item.quantity}" aria-label="One-time or subscription">
              <optgroup label="Pay full price">
                <option value="onetime">One-time only</option>
              </optgroup>
              <optgroup label="Subscribe and save ${subPct}%">
                ${planOpts}
              </optgroup>
            </select>
          </div>`;
        } else {
          subscribeHtml = `
          <div class="cart-item-subscribe">
            <button type="button" class="upgrade-btn" data-key="${escHtml(item.key)}" data-qty="${item.quantity}">
              Subscribe &amp; Save ${subPct}% on all future orders
            </button>
          </div>`;
        }
      } else if (hasPlan) {
        const rawName = item.selling_plan_allocation.selling_plan?.name || '';
        const one = enrichSellingPlanLabels(
          [{ id: currentPlanId, name: rawName }],
          container,
        );
        const disp = one[0]?.name || rawName || 'Subscription';
        subscribeHtml = `<div class="cart-item-subscribe"><span class="sub-pill">${escHtml(disp)}</span></div>`;
      }

      const discountHtml = showDiscount
        ? `<p class="cart-item-promo">${offPct}% off applied</p>`
        : '';

      const priceHtml = showDiscount
        ? `<span class="was" aria-hidden="true">${ZZAI.fmt(orig)}</span><span class="now">${ZZAI.fmt(fin)}</span>`
        : `<span class="now">${ZZAI.fmt(fin)}</span>`;

      const mult = Math.max(1, parseInt(String(item.properties?._pack_mult || '1'), 10) || 1);
      const dispQty = mult > 1 ? Math.round(item.quantity / mult) : item.quantity;
      const isPledgeReward =
        String(item.properties?._pledge_entitlement || '').trim() === '1' ||
        String(item.properties?._pledge_swag || '').trim() === '1';
      const qtyHtml = isPledgeReward
        ? `<div class="qty qty--locked" title="Kickstarter reward quantity is locked">
              <span class="qty-btn" aria-hidden="true">-</span>
              <span class="n">${dispQty}</span>
              <span class="qty-btn" aria-hidden="true">+</span>
            </div>
            <span class="pledge-locked-note">Reward quantity locked</span>`
        : `<div class="qty">
              <button type="button" class="qty-btn qty-minus" data-key="${escHtml(item.key)}" data-qty="${item.quantity}" data-step="${mult}" aria-label="Decrease quantity">-</button>
              <span class="n">${dispQty}</span>
              <button type="button" class="qty-btn qty-plus" data-key="${escHtml(item.key)}" data-qty="${item.quantity}" data-step="${mult}" aria-label="Increase quantity">+</button>
            </div>`;

      return `
      <div class="cart-item zzai-brand-root${hasPlan ? ' cart-item--subscribed' : ''}"${skuAttr}>
        <div class="cart-item-img zzai-flavor-pop zzai-flavor-pop--contain zzai-flavor-pop--plain">${imgBlock}</div>
        <div class="cart-item-body">
          <div class="cart-item-head">
            <div class="cart-item-copy">
              <h4 class="name">${titleHtml}</h4>
              ${subHtml}
              ${discountHtml}
            </div>
            <div class="cart-item-price">${priceHtml}</div>
          </div>
          <div class="cart-item-actions">
            ${qtyHtml}
            <button type="button" class="remove cart-line__remove" data-key="${escHtml(item.key)}">Remove</button>
          </div>
          ${subscribeHtml}
        </div>
      </div>`;
    }),
  );

  container.innerHTML = rows.join('');
}

function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeImgSrc(url) {
  if (!url) return '';
  const s = String(url).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith('//') || s.startsWith('/')) return s;
  return '';
}

/* ─── Subscribe nudge ────────────────────────────────────────────────────── */

function readCommerceJson() {
  const node = document.getElementById('zzai-commerce-config');
  if (!node) return null;
  try {
    const parsed = JSON.parse(String(node.textContent || '').trim());
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Subscription % from Plans (theme settings). Commerce JSON + global config + cart data attributes. */
function readSubDiscountLabel() {
  const commerce = readCommerceJson();
  const fromCommerce = String(commerce?.sub_pct_label ?? '').trim();
  if (fromCommerce && fromCommerce !== '0') return fromCommerce;
  const commercePct = ZZAI.fmtPct(commerce?.sub_pct);
  if (commercePct && commercePct !== '0') return commercePct;

  const cfg = ZZAI.getConfig();
  const label = String(cfg.subDiscountLabel || '').trim();
  if (label && label !== '0') return label;
  const fromCfg = ZZAI.fmtPct(cfg.subDiscount);
  if (fromCfg && fromCfg !== '0') return fromCfg;

  const section = document.querySelector('.zzai-cart-section');
  const fromSection = String(section?.dataset?.subDiscountLabel ?? '').trim();
  if (fromSection && fromSection !== '0') return fromSection;

  const lines = document.getElementById('zzai-cart-lines');
  const fromLines = String(lines?.dataset?.subDiscount ?? '').trim();
  return fromLines || '0';
}

function updateSubscribeNudge(cart) {
  const nudge = document.getElementById('zzai-sub-nudge');
  if (!nudge) return;
  const hasItems = Array.isArray(cart.items) && cart.items.length > 0;
  const hasSubscription =
    hasItems && cart.items.some(item => item.selling_plan_allocation != null);
  const oneTimeCount = hasItems
    ? cart.items.filter(item => !item.selling_plan_allocation).length
    : 0;
  nudge.hidden = !hasItems || hasSubscription || oneTimeCount <= 1;
  const pct = readSubDiscountLabel();
  nudge.textContent = `Subscribe & Save ${pct}% on all future orders`;
}

function readFlatShippingCents() {
  const commerce = readCommerceJson();
  const fromCommerce = parseInt(commerce?.flat_cents ?? '', 10);
  if (Number.isFinite(fromCommerce) && fromCommerce > 0) return fromCommerce;

  const section = document.querySelector('.zzai-cart-section');
  const fromSection = parseInt(section?.dataset?.flatShippingCents ?? '', 10);
  if (Number.isFinite(fromSection) && fromSection > 0) return fromSection;

  const cfg = ZZAI.getConfig();
  if (cfg.flatShippingCents > 0) return cfg.flatShippingCents;
  const fromCfgDollars = ZZAI.dollarsToCents(cfg.flatShipping);
  if (fromCfgDollars > 0) return fromCfgDollars;

  const bar = document.getElementById('zzai-shipping-bar');
  const fromBar = parseInt(bar?.dataset?.flatShippingCents ?? '', 10);
  return Number.isFinite(fromBar) && fromBar > 0 ? fromBar : 0;
}

function shipFlatCents() {
  const cents = readFlatShippingCents();
  return cents > 0 ? cents : 0;
}

/** Integer cents — commerce JSON, global config, cart section, shipping bar. */
function readFreeShippingThresholdCents() {
  const commerce = readCommerceJson();
  const fromCommerce = parseInt(commerce?.fs_cents ?? '', 10);
  if (Number.isFinite(fromCommerce) && fromCommerce > 0) return fromCommerce;

  const section = document.querySelector('.zzai-cart-section');
  const fromSection = parseInt(section?.dataset?.freeShippingCents ?? '', 10);
  if (Number.isFinite(fromSection) && fromSection > 0) return fromSection;

  const cfg = ZZAI.getConfig();
  if (cfg.freeShippingCents > 0) return cfg.freeShippingCents;
  const fromCfgDollars = ZZAI.dollarsToCents(cfg.freeShipping);
  if (fromCfgDollars > 0) return fromCfgDollars;

  const bar = document.getElementById('zzai-shipping-bar');
  const fromBar = parseInt(bar?.dataset?.thresholdCents ?? '', 10);
  if (Number.isFinite(fromBar) && fromBar > 0) return fromBar;

  return 0;
}

function subscriptionFreeShippingEnabled() {
  const commerce = readCommerceJson();
  if (commerce && typeof commerce.subscription_free_shipping === 'boolean') {
    return commerce.subscription_free_shipping;
  }
  if (commerce && typeof commerce.subscriptionFreeShipping === 'boolean') {
    return commerce.subscriptionFreeShipping;
  }

  const section = document.querySelector('.zzai-cart-section');
  const fromSection = String(section?.dataset?.subscriptionFreeShipping || '').trim().toLowerCase();
  if (fromSection) return fromSection === 'true' || fromSection === '1' || fromSection === 'yes';

  const cfg = document.getElementById('zzai-global-config');
  const fromGlobal = String(cfg?.dataset?.subscriptionFreeShipping || '').trim().toLowerCase();
  if (fromGlobal) return fromGlobal === 'true' || fromGlobal === '1' || fromGlobal === 'yes';

  return false;
}

function cartHasSubscriptionLine(cart) {
  return Array.isArray(cart?.items) && cart.items.some((item) => item.selling_plan_allocation != null);
}

/** Store-wide by default; optional admin toggle lets subscriptions ship free. */
function cartQualifiesFreeShipping(cart, thresholdCents) {
  if (!cart?.item_count) return false;
  if (subscriptionFreeShippingEnabled() && cartHasSubscriptionLine(cart)) return true;
  if (!thresholdCents || thresholdCents <= 0) return false;
  return (Number(cart.total_price) || 0) >= thresholdCents;
}

function updateCartHeader(cart) {
  const countEl = document.getElementById('zzai-cart-count');
  if (!countEl) return;
  const n = Number(cart.item_count) || 0;
  countEl.textContent = '· ' + n;
}

function updateCartTotals(cart) {
  const subEl = document.getElementById('zzai-cart-subtotal');
  const shipEl = document.getElementById('zzai-cart-shipping');
  const totalEl = document.getElementById('zzai-cart-total');
  const foot = document.getElementById('zzai-cart-foot');
  const meter = document.getElementById('zzai-shipping-bar');
  const hasItems = Array.isArray(cart.items) && cart.items.length > 0;
  const cartRoot = document.querySelector('.zzai-cart-section');
  const isB2B = cartRoot?.dataset.b2b === 'true';
  const minimumCents = Number(cartRoot?.dataset.wholesaleMinimumCents || 0);
  const accountType = String(cartRoot?.dataset.wholesaleAccountType || 'wholesale').replaceAll('_', ' ');

  const thresholdCentsForMeter = readFreeShippingThresholdCents();
  if (foot) foot.hidden = !hasItems;
  if (meter) meter.hidden = isB2B || !hasItems || thresholdCentsForMeter <= 0;

  const subtotalCents = Number(cart.total_price) || 0;
  const thresholdCents = readFreeShippingThresholdCents();
  const shipCents =
    !hasItems || subtotalCents === 0 || cartQualifiesFreeShipping(cart, thresholdCents) ? 0 : shipFlatCents();
  const totalCents = isB2B ? subtotalCents : subtotalCents + shipCents;

  if (subEl) subEl.textContent = ZZAI.fmt(subtotalCents);
  // Names the zone deliberately, matching the drawer's Liquid first render:
  // the flat rate and the free-over-threshold only apply to the East & Central
  // zone (38 states). The Western states, AK, HI, PR and the territories are
  // quoted live by the carrier, and the cart cannot know the destination yet —
  // a bare "Included" or "$9.95" overpromises to those shoppers and the real
  // rate only surfaces once they enter an address at checkout.
  if (shipEl) {
    shipEl.textContent = isB2B
      ? 'Calculated at checkout'
      : shipCents === 0
        ? 'Free · East & Central'
        : `${ZZAI.fmt(shipCents)} · East & Central`;
  }
  const shipNote = document.getElementById('zzai-cart-ship-note');
  if (shipNote) shipNote.hidden = isB2B || !hasItems;
  if (totalEl) totalEl.textContent = ZZAI.fmt(totalCents);

  const note = document.getElementById('zzai-wholesale-minimum-note');
  const checkout = document.querySelector('[data-cart-checkout]');
  const shortfall = Math.max(0, minimumCents - subtotalCents);
  const blocked = isB2B && hasItems && minimumCents > 0 && shortfall > 0;
  if (note) {
    note.hidden = !blocked;
    if (blocked) note.textContent = `${accountType.charAt(0).toUpperCase()}${accountType.slice(1)} orders require a ${ZZAI.fmt(minimumCents)} merchandise minimum. Add ${ZZAI.fmt(shortfall)} more to check out.`;
  }
  if (checkout) {
    checkout.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    checkout.classList.toggle('is-disabled', blocked);
    checkout.title = blocked ? 'Order minimum not reached' : '';
    checkout.textContent = blocked ? `Add ${ZZAI.fmt(shortfall)} more to check out` : 'Checkout';
    checkout.href = blocked ? '/pages/wholesale-order' : (window.ZZAI?.checkoutUrl?.() || '/checkout');
  }
}
function updateShippingBar(cart) {
  const bar = document.getElementById('zzai-shipping-bar');
  const fill = document.getElementById('zzai-bar-fill');
  const label = document.getElementById('zzai-bar-label');
  if (!bar || !fill || !label) return;

  const thresholdCents = readFreeShippingThresholdCents();
  const isB2B = document.querySelector('.zzai-cart-section')?.dataset.b2b === 'true';
  const hasItems = Array.isArray(cart?.items) && cart.items.length > 0;
  bar.hidden = isB2B || !hasItems || thresholdCents <= 0;
  if (bar.hidden) return;
  const subtotalCents = Number(cart.total_price) || 0;
  const qualifies = cartQualifiesFreeShipping(cart, thresholdCents);
  const remainingCents =
    thresholdCents > 0 ? Math.max(0, thresholdCents - subtotalCents) : 0;
  const pct =
    qualifies || !cart.item_count
      ? 100
      : thresholdCents > 0
        ? Math.min((subtotalCents / thresholdCents) * 100, 100)
        : 0;

  fill.style.width = pct + '%';
  // Tray-count messaging first (one tray = one more to go, 2+ = unlocked);
  // non-tray carts keep the dollar-remaining line with the East & Central
  // qualifier, since those carts cross the threshold in dollars, not trays.
  const TRAY_SKUS = ['ZZ-CHP-12', 'ZZ-ZOB-12', 'ZZ-TAM-12', 'ZZ-OBI-12', 'ZZ-MIX-12'];
  const trayCount = (cart.items || []).reduce(
    (n, item) => n + (TRAY_SKUS.includes(item.sku) ? item.quantity : 0), 0);
  if (trayCount >= 2) {
    label.innerHTML =
      '<span style="color:var(--zzai-gold);margin-right:6px;">✦</span>You&rsquo;ve unlocked free shipping.';
  } else if (trayCount === 1 && !qualifies) {
    label.innerHTML = 'You&rsquo;re one tray away from free shipping.';
  } else if (qualifies) {
    label.innerHTML =
      '<span style="color:var(--zzai-gold);margin-right:6px;">✦</span>Ships free across the East &amp; Central U.S.';
  } else if (thresholdCents > 0) {
    label.innerHTML = `<strong>${ZZAI.fmt(remainingCents)}</strong> away from FREE shipping &middot; East &amp; Central`;
  } else {
    label.innerHTML = '<span>Add items to see shipping progress.</span>';
  }
}

/* ─── Cart mutations (debounced to avoid rapid-fire API calls) ────────────── */

/* --- Discount guard ------------------------------------------------------- */

function lineHasPledgeMarker(item) {
  const p = item?.properties || {};
  return (
    String(p._pledge_entitlement || '').trim() === '1' ||
    String(p._pledge_upsell || '').trim() === '1' ||
    String(p._pledge_swag || '').trim() === '1'
  );
}

function cartHasPledgeMarker(cart) {
  return Array.isArray(cart?.items) && cart.items.some(lineHasPledgeMarker);
}

function cartHasCartLevelDiscount(cart) {
  return (
    (Array.isArray(cart?.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0) ||
    (Array.isArray(cart?.discount_codes) && cart.discount_codes.length > 0)
  );
}

async function readCartJson() {
  const res = await fetch(ZZAI.cartJsUrl(), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const text = await res.text();
  if (!res.ok) throw cartApiError(text.slice(0, 240) || `${res.status} ${res.statusText}`.trim(), res, text);
  return parseCartJsonBody(text, res);
}

async function changeQty(key, newQty) {
  try {
    await cartJsonPost(ZZAI.cartChangeJsUrl(), { id: key, quantity: newQty });
    await refreshCart();
  } catch (err) {
    console.error('[zzai-cart] changeQty failed:', err);
    window.alert(ZZAI.cartFetchErrorMessage(err));
  }
}

const debouncedChangeQty = ZZAI.debounce(changeQty, 300);

function removeItem(key) {
  changeQty(key, 0); // remove is intentional and immediate — no debounce
}

async function upgradeAllOneTimeLines() {
  const container = document.getElementById('zzai-cart-lines');
  let cart;
  try {
    const res = await fetch(ZZAI.cartJsUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    const text = await res.text();
    if (!res.ok) throw new Error(text.slice(0, 200) || `${res.status}`);
    cart = parseCartJsonBody(text, res);
  } catch (err) {
    console.error('[zzai-cart] upgradeAllOneTimeLines: could not read cart', err);
    alert(zzaiCartCustomerUpdateError());
    return;
  }

  const targets = (cart.items || []).filter(item => !item.selling_plan_allocation);
  if (!targets.length) return;

  let upgraded = 0;
  for (const item of targets) {
    const planStr = await pickSellingPlanForLine(item, container);
    if (!planStr) continue;
    const selling_plan = sellingPlanForApi(planStr);
    try {
      await cartJsonPost(ZZAI.cartChangeJsUrl(), {
        id: item.key,
        quantity: parseInt(item.quantity, 10),
        selling_plan,
      });
      upgraded += 1;
    } catch (err) {
      console.error('[zzai-cart] upgradeAllOneTimeLines line failed:', err);
    }
  }

  await refreshCart();

  if (upgraded === 0) {
    alert(zzaiCartCustomerUpdateError());
  }
}

async function upgradeToSubscription(key, qty) {
  const container = document.getElementById('zzai-cart-lines');
  let cart;
  try {
    const res = await fetch(ZZAI.cartJsUrl(), {
      credentials: 'same-origin',
      headers:     { Accept: 'application/json' },
    });
    const text = await res.text();
    if (!res.ok) throw new Error(text.slice(0, 200) || `${res.status}`);
    cart = parseCartJsonBody(text, res);
  } catch (err) {
    console.error('[zzai-cart] upgradeToSubscription: could not read cart', err);
    alert(zzaiCartCustomerUpdateError());
    return;
  }

  const item = (cart.items || []).find(i => i.key === key);
  if (!item) {
    alert(zzaiCartCustomerUpdateError());
    return;
  }

  const planStr = await pickSellingPlanForLine(item, container);
  if (!planStr) {
    alert(zzaiCartCustomerUpdateError());
    return;
  }

  const selling_plan = sellingPlanForApi(planStr);
  try {
    await cartJsonPost(ZZAI.cartChangeJsUrl(), {
      id:           key,
      quantity:     parseInt(qty, 10),
      selling_plan,
    });
    await refreshCart();
  } catch (err) {
    console.error('[zzai-cart] upgradeToSubscription failed:', err);
    alert(zzaiCartCustomerUpdateError());
  }
}

/* ─── Cross-sells ─────────────────────────────────────────────────────────── */

function crossSellHandles() {
  const raw =
    document.querySelector('.zzai-cart-section')?.dataset?.crossSellHandles?.trim() ||
    document.getElementById('zzai-global-config')?.dataset?.crossSellHandles?.trim();
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  for (const h of raw.split(',').map(s => s.trim()).filter(Boolean)) {
    if (seen.has(h)) continue;
    seen.add(h);
    out.push(h);
  }
  return out;
}

function productCardImageUrl(p) {
  const im = p.images?.[0] ?? p.featured_image;
  if (!im) return '';
  if (typeof im === 'string') return im;
  return im.src || '';
}

function firstAvailableVariant(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  return (
    variants.find(v => v && v.available === true) ||
    variants.find(v => v && v.available !== false) ||
    variants[0] ||
    null
  );
}

function crossSellEnabled() {
  const root = document.querySelector('.zzai-cart-section');
  return root?.dataset?.showCrossSell !== 'false';
}

function toggleCrossSellVisibility(cart) {
  const block = document.getElementById('cart-cross-sell-block');
  if (!block) return;
  const empty = !cart.items || cart.items.length === 0;
  block.hidden = empty || !crossSellEnabled();
}

function updateCrossSellNavVisibility() {
  const rail = document.getElementById('cart-cross-sells');
  const nav = document.getElementById('cart-cross-sell-nav');
  if (!rail || !nav) return;
  requestAnimationFrame(() => {
    const prev = nav.querySelector('.cross-sell-nav__btn[data-dir="-1"]');
    const next = nav.querySelector('.cross-sell-nav__btn[data-dir="1"]');
    const hasCards = crossSellCardCount(rail) > 0;
    if (!hasCards) {
      nav.hidden = true;
      return;
    }
    const canScroll = rail.scrollWidth > rail.clientWidth + 6;
    nav.hidden = !canScroll;
    if (!canScroll) {
      prev?.removeAttribute('disabled');
      next?.removeAttribute('disabled');
      return;
    }
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const edge = 6;
    const atStart = rail.scrollLeft <= edge;
    const atEnd = rail.scrollLeft >= maxScroll - edge;
    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
  });
}

function setupCrossSellNavOnce() {
  const block = document.getElementById('cart-cross-sell-block');
  if (!block || block.dataset.navInit === '1') return;
  block.dataset.navInit = '1';
  block.addEventListener('click', e => {
    const t = e.target.closest('.cross-sell-nav__btn[data-dir]');
    if (!t || t.disabled) return;
    const rail = document.getElementById('cart-cross-sells');
    if (!rail) return;
    const dir = parseInt(t.dataset.dir, 10);
    const step = Math.min(220, Math.floor(rail.clientWidth * 0.85) || 220);
    rail.scrollBy({ left: dir * step, behavior: 'smooth' });
  });
  window.addEventListener('resize', updateCrossSellNavVisibility, { passive: true });

  const rail = document.getElementById('cart-cross-sells');
  if (rail && rail.dataset.zzaiNavBound !== '1') {
    rail.dataset.zzaiNavBound = '1';
    rail.addEventListener('scroll', updateCrossSellNavVisibility, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => updateCrossSellNavVisibility()).observe(rail);
    }
  }
}

function crossSellCardCount(rail) {
  return rail ? rail.querySelectorAll('.cross-sell-card').length : 0;
}

function setCrossSellEmpty(rail, message) {
  if (!rail) return;
  rail.innerHTML = `<p class="cross-sell-empty">${escHtml(message)}</p>`;
  rail.removeAttribute('data-ssr');
  lastCrossSellHandlesKey = '';
  updateCrossSellNavVisibility();
}

async function loadCrossSells(cart) {
  const rail = document.getElementById('cart-cross-sells');
  const block = document.getElementById('cart-cross-sell-block');
  if (!rail || !crossSellEnabled()) return;

  const handles = crossSellHandles();
  if (handles.length === 0) {
    if (crossSellCardCount(rail) === 0) {
      setCrossSellEmpty(rail, 'Suggested flavors are not configured in theme settings.');
      if (block) block.hidden = true;
    }
    return;
  }

  const cartHandles = new Set(
    (cart.items || []).map(i => String(i.handle || i.product_handle || '').trim()).filter(Boolean),
  );
  const candidates = handles.filter(h => !cartHandles.has(h)).slice(0, 8);
  const candidatesKey = candidates.join('|');

  if (candidates.length === 0) {
    setCrossSellEmpty(rail, 'All suggested flavors are in your cart.');
    if (block) block.hidden = false;
    return;
  }

  if (block) block.hidden = false;

  if (candidatesKey === lastCrossSellHandlesKey && crossSellCardCount(rail) > 0) {
    updateCrossSellNavVisibility();
    return;
  }

  if (rail.dataset.ssr === '1' && crossSellCardCount(rail) > 0) {
    lastCrossSellHandlesKey = candidatesKey;
    updateCrossSellNavVisibility();
    return;
  }

  const results = await Promise.all(candidates.map(h => getProductJson(h)));
  const products = results.filter(Boolean);

  if (products.length === 0) {
    console.warn('[zzai-cart] cross-sells unavailable');
    if (crossSellCardCount(rail) > 0) {
      lastCrossSellHandlesKey = candidatesKey;
      updateCrossSellNavVisibility();
      return;
    }
    setCrossSellEmpty(rail, 'Suggested flavors could not be loaded. Refresh or open the shop.');
    return;
  }

  const cards = products
    .map(p => {
      const variant = firstAvailableVariant(p);
      if (!variant) return null;
      const img = safeImgSrc(productCardImageUrl(p));
      const title = escHtml(displayNameForHandle(p.handle, p));
      const price = ZZAI.fmt(variant.price);
      const sku = ZZAI.flavorSkuFromHandle(p.handle);
      const skuAttr = sku ? ` data-sku="${escHtml(sku)}"` : '';
      return `
      <div class="cross-sell-card zzai-brand-root"${skuAttr}>
        <div class="cross-sell-card__thumb">
          <div class="cross-sell-card__accent" aria-hidden="true"></div>
          <div class="cross-sell-card__media zzai-flavor-pop zzai-flavor-media-frame zzai-flavor-pop--contain">
            ${img ? `<img src="${escHtml(img)}" alt="${title}" loading="lazy" width="120" height="120">` : ''}
          </div>
        </div>
        <div class="cross-sell-card__body">
          <p class="cross-sell-card__name">${title}</p>
          <p class="cross-sell-card__price">${price}</p>
          <button type="button" class="add-btn" data-variant="${variant.id}" data-qty="1">Add</button>
        </div>
      </div>`;
    })
    .filter(Boolean);

  if (cards.length === 0) {
    lastCrossSellHandlesKey = '';
    rail.innerHTML = '<p class="cross-sell-empty">Suggested flavors are sold out right now.</p>';
    updateCrossSellNavVisibility();
    return;
  }

  rail.innerHTML = cards.join('');
  rail.removeAttribute('data-ssr');

  lastCrossSellHandlesKey = candidatesKey;
  updateCrossSellNavVisibility();
}

/* ─── DOMContentLoaded ────────────────────────────────────────────────────── */

/**
 * Build `{ items: [...] }` from a standard product add form, or null if we should not take over.
 * Forms with line-item `properties` stay on FormData (gift cards, engravings, etc.).
 */
function productFormToCartItemsPayload(form) {
  if (form.querySelector('[name^="properties"], [name="properties"]')) return { useFormData: true };
  const idInp = form.querySelector('input[name="id"], select[name="id"]');
  if (!idInp || !String(idInp.value).trim()) return null;
  const id = parseInt(String(idInp.value).trim(), 10);
  if (!Number.isFinite(id) || id <= 0) return null;
  const qInp = form.querySelector('input[name="quantity"], select[name="quantity"]');
  const qty = Math.max(1, parseInt(String(qInp && qInp.value != null ? qInp.value : '1'), 10) || 1);
  const item = { id, quantity: qty };
  const spInp = form.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
  if (spInp && spInp.value) {
    const sp = parseInt(String(spInp.value).trim(), 10);
    if (Number.isFinite(sp) && sp > 0) item.selling_plan = sp;
  }
  return { items: [item] };
}

function parseCartAddResponse(res, text) {
  if (!res.ok) throw cartApiError(text.slice(0, 240) || res.statusText || String(res.status), res, text);
  let body = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = {};
  }
  if (body && typeof body.status === 'number' && body.status >= 400) {
    throw cartApiError(body.description || body.message || 'Cart add rejected', { status: body.status }, text);
  }
  return body;
}

function dispatchConfirmedCartAdd(variantId, source = 'product-form-add') {
  document.dispatchEvent(
    new CustomEvent(THEME_CART_UPDATE, {
      bubbles: true,
      detail: {
        resource: null,
        sourceId: 'zzai-cart',
        data: {
          variantId: String(variantId || ''),
          source,
        },
      },
    }),
  );
}

document.addEventListener('DOMContentLoaded', () => {
  setupCrossSellNavOnce();
  setupZzaiCartDelegationOnce();
  document.addEventListener(THEME_CART_UPDATE, event => {
    const data = event?.detail?.data || {};
    if (data.didError || data.source !== 'product-form-component') return;
    openCartDrawer();
  });

  // Intercept add-to-cart form submits (Horizon quick-add, product forms). Prefer JSON
  // `items` POST — same as PDP — so behavior matches Shopify's Cart AJAX API reliably.
  document.addEventListener('submit', e => {
    const form = e.target instanceof HTMLFormElement ? e.target : e.target.closest?.('form');
    if (!form) return;
    const rawAction = (form.getAttribute('action') || '').trim().toLowerCase();
    if (!rawAction.includes('/cart/add')) return;

    const addUrl = ZZAI.cartAddJsUrl();
    const payload = productFormToCartItemsPayload(form);
    if (!payload) return;

    e.preventDefault();

    if (payload.useFormData) {
      const data = new FormData(form);
      const variantId = data.get('id');
      fetch(addUrl, {
        method: 'POST',
        body: data,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then(async res => {
          const text = await res.text();
          parseCartAddResponse(res, text);
        })
        .then(() => {
          dispatchConfirmedCartAdd(variantId, 'product-form-data-add');
          openCartDrawer();
        })
        .catch(err => {
          console.error('[zzai-cart] add failed:', err);
          window.alert(ZZAI.cartFetchErrorMessage(err));
        });
      return;
    }

    ZZAI.jsonPost(addUrl, payload)
      .then(async res => {
        const text = await res.text();
        parseCartAddResponse(res, text);
      })
      .then(() => {
        dispatchConfirmedCartAdd(payload.items?.[0]?.id, 'product-form-json-add');
        openCartDrawer();
      })
      .catch(err => {
        console.error('[zzai-cart] add failed:', err);
        window.alert(ZZAI.cartFetchErrorMessage(err));
      });
  });
});

/* ─── Intercept Horizon's native cart icon ────────────────────────────────── */

function interceptCartLinks() {
  document.addEventListener(
    'click',
    e => {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.toLowerCase().startsWith('javascript:')) return;
      if (link.closest('.cart-foot, .cart-footer')) return;
      if (link.dataset.zzaiCartPage === 'true') return;
      if (link.closest('a[href*="/checkout"]')) return;

      let path = '';
      try {
        path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') || '/';
      } catch {
        return;
      }

      const cartPath = ZZAI.cartPagePath();
      if (path !== cartPath) return;

      e.preventDefault();
      openCartDrawer();
    },
    true,
  );
}

/* ─── Global exposure ─────────────────────────────────────────────────────── */

window.openCartDrawer  = openCartDrawer;
window.closeCartDrawer = closeCartDrawer;
window.refreshCart     = refreshCart;

interceptCartLinks();

/**
 * Horizon opens <cart-drawer-component> from the header; ZZAI cross-sell + upgrade live in
 * #zzai-cart-drawer. Hijack the header trigger so customers always get one consistent drawer.
 */
function hijackThemeCartDrawerTrigger() {
  if (!document.getElementById('zzai-cart-drawer')) return;

  /* window + capture runs before most theme handlers on document / bubble targets */
  window.addEventListener(
    'click',
    e => {
      const trigger = e.target.closest('button[data-testid="cart-drawer-trigger"]');
      if (!trigger) return;
      if (trigger.closest('#zzai-cart-drawer')) return;
      const themeDrawer = trigger.closest('cart-drawer-component');
      if (!themeDrawer) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      try {
        if (typeof themeDrawer.close === 'function') themeDrawer.close();
      } catch (_) {
        /* no-op */
      }
      const dlg = themeDrawer.querySelector('dialog');
      if (dlg?.open) dlg.close();

      openCartDrawer();
    },
    true,
  );
}

hijackThemeCartDrawerTrigger();
setupZzaiCartDelegationOnce();
